import React from 'react';
import { Lock, Building2, Fingerprint, AlertTriangle, Shield, FileText } from 'lucide-react';

const SecuritySection = () => {
  const features = [
    {
      icon: Lock,
      title: 'AES-256 Encryption',
      description: 'All your financial data encrypted at rest and in transit with TLS 1.3',
    },
    {
      icon: Building2,
      title: 'Zero Credential Storage',
      description: 'Your bank login is never stored by Findell. Plaid handles all bank connections.',
    },
    {
      icon: Fingerprint,
      title: 'Biometric Authentication',
      description: 'Face ID and Touch ID support for instant secure access',
    },
    {
      icon: AlertTriangle,
      title: 'Suspicious Login Detection',
      description: 'Instant alerts when your account is accessed from a new device',
    },
    {
      icon: Shield,
      title: 'Annual Security Audits',
      description: 'Third-party penetration testing every year — results published publicly',
    },
    {
      icon: FileText,
      title: 'Cyber Liability Insurance',
      description: 'Full coverage for breach notification, legal fees, and user remediation',
    },
  ];

  const trustBadges = [
    { text: 'Powered by Plaid' },
    { text: 'Payments by RevenueCat' },
    { text: 'Secured by AES-256' },
    { text: 'CCPA Compliant' },
  ];

  return (
    <section
      id="security"
      data-testid="security-section"
      className="bg-[#0A1628] section-padding"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p
            data-testid="security-section-label"
            className="font-body text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#D4AF37] mb-4"
          >
            Bank-Level Security
          </p>
          <h2
            data-testid="security-section-headline"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FAFAF7] leading-tight"
          >
            Fort Knox Security.
            <br />
            Human-First Experience.
          </h2>
        </div>

        {/* Security Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              data-testid={`security-feature-${index}`}
              className="bg-[#0F1F3D] rounded-2xl p-6 border border-[#D4AF37]/10 transition-all duration-300 hover:border-[#D4AF37]/30 animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h4 className="font-heading text-lg font-bold text-[#FAFAF7] mb-2">
                {feature.title}
              </h4>
              <p className="font-body text-sm text-[#FAFAF7]/70 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 pt-8 border-t border-[#D4AF37]/20">
          {trustBadges.map((badge, index) => (
            <span
              key={badge.text}
              data-testid={`trust-badge-${index}`}
              className="font-body text-sm text-[#FAFAF7]/60 flex items-center gap-2"
            >
              <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
              {badge.text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SecuritySection;
